import { k as jsxRuntimeExports, an as roundForDisplay, o as Context, u as useRecordVisibilityChanges } from "./doenetml-drNYjJiF.js";
import React__default, { useState, useRef, useEffect, useMemo, createContext } from "react";
import { u as useDoenetRenderer } from "./useDoenetRenderer-CDZbHBy-.js";
import Prefigure from "./prefigure-UIeHl-FK.js";
import { a as accessibleLabelText, r as renderLabelWithLatex } from "./labelWithLatex-tld42Q2B.js";
import GraphFrame from "./GraphFrame-5R_Nnklj.js";
import { J as JSXGraphRenderer } from "./JSXGraphRenderer-BcR36ozd.js";
const GRAPH_CONTROL_CARD_STYLE = {
  width: "100%",
  boxSizing: "border-box",
  padding: "10px",
  border: "1px solid var(--canvasText)",
  borderRadius: "8px"
};
const GRAPH_CONTROL_HEADING_STYLE = {
  fontWeight: 600,
  margin: 0,
  fontSize: "1em",
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: "8px"
};
const GRAPH_CONTROL_HEADING_TEXT_STYLE = {
  minWidth: 0
};
const GRAPH_CONTROL_DISCLOSURE_BUTTON_STYLE = {
  border: "1px solid var(--canvasText)",
  borderRadius: "4px",
  backgroundColor: "transparent",
  color: "inherit",
  width: "1.65em",
  height: "1.65em",
  minWidth: "1.65em",
  padding: 0,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  transition: "background-color 120ms ease, box-shadow 120ms ease"
};
const GRAPH_CONTROL_DISCLOSURE_BUTTON_HOVER_STYLE = {
  backgroundColor: "rgba(127, 127, 127, 0.15)"
};
const GRAPH_CONTROL_DISCLOSURE_BUTTON_PRESSED_STYLE = {
  backgroundColor: "rgba(127, 127, 127, 0.25)",
  boxShadow: "inset 0 0 0 1px var(--canvasText)"
};
const GRAPH_CONTROL_DISCLOSURE_BUTTON_FOCUS_STYLE = {
  boxShadow: "0 0 0 2px var(--mainBlue)"
};
const GRAPH_CONTROL_DISCLOSURE_ICON_STYLE = {
  display: "inline-block",
  width: 0,
  height: 0,
  borderStyle: "solid"
};
const GRAPH_CONTROL_DISCLOSURE_ICON_EXPANDED_STYLE = {
  borderWidth: "6px 5px 0 5px",
  borderColor: "var(--canvasText) transparent transparent transparent"
};
const GRAPH_CONTROL_DISCLOSURE_ICON_COLLAPSED_STYLE = {
  borderWidth: "5px 0 5px 6px",
  borderColor: "transparent transparent transparent var(--canvasText)"
};
const GRAPH_CONTROL_CONTENT_STYLE = {
  marginTop: "6px"
};
const GRAPH_CONTROL_INPUT_BLOCK_STYLE = {
  display: "flex",
  flexDirection: "column",
  gap: "4px",
  marginTop: "8px"
};
const GRAPH_CONTROL_INLINE_INPUT_STACK_STYLE = {
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
  width: "100%"
};
const GRAPH_CONTROL_TEXT_INPUT_STYLE = {
  width: "100%",
  boxSizing: "border-box",
  minWidth: 0
};
const GRAPH_CONTROL_ERROR_TEXT_STYLE = {
  color: "var(--errorText)",
  fontSize: "0.85em"
};
const GRAPH_CONTROL_SECTION_HEADING_STYLE = {
  fontWeight: 600,
  margin: "8px 0 2px 0",
  fontSize: "0.9em"
};
const GRAPH_CONTROL_SECTION_HEADING_WITH_DIVIDER_STYLE = {
  ...GRAPH_CONTROL_SECTION_HEADING_STYLE,
  marginTop: "12px",
  paddingTop: "8px",
  borderTop: "1px solid var(--canvasText)"
};
function ControlCard({
  id,
  headingId,
  heading,
  children,
  contentId,
  isExpanded = true,
  onToggleExpanded,
  disclosureControlLabel
}) {
  const resolvedContentId = contentId ?? (id ? `${id}-content` : void 0);
  const [isHeadingButtonFocused, setIsHeadingButtonFocused] = React__default.useState(false);
  const [isHeadingButtonHovered, setIsHeadingButtonHovered] = React__default.useState(false);
  const [isHeadingButtonPressed, setIsHeadingButtonPressed] = React__default.useState(false);
  const disclosureButtonStyle = {
    ...GRAPH_CONTROL_DISCLOSURE_BUTTON_STYLE,
    ...isHeadingButtonHovered ? GRAPH_CONTROL_DISCLOSURE_BUTTON_HOVER_STYLE : {},
    ...isHeadingButtonPressed ? GRAPH_CONTROL_DISCLOSURE_BUTTON_PRESSED_STYLE : {},
    ...isHeadingButtonFocused ? GRAPH_CONTROL_DISCLOSURE_BUTTON_FOCUS_STYLE : {}
  };
  const disclosureIconStyle = isExpanded ? {
    ...GRAPH_CONTROL_DISCLOSURE_ICON_STYLE,
    ...GRAPH_CONTROL_DISCLOSURE_ICON_EXPANDED_STYLE
  } : {
    ...GRAPH_CONTROL_DISCLOSURE_ICON_STYLE,
    ...GRAPH_CONTROL_DISCLOSURE_ICON_COLLAPSED_STYLE
  };
  const disclosureControlLabelSuffix = disclosureControlLabel ? ` for ${disclosureControlLabel}` : "";
  return /* @__PURE__ */ jsxRuntimeExports.jsxs(
    "div",
    {
      id,
      role: "group",
      "aria-labelledby": headingId,
      style: GRAPH_CONTROL_CARD_STYLE,
      children: [
        /* @__PURE__ */ jsxRuntimeExports.jsxs("h3", { id: headingId, style: GRAPH_CONTROL_HEADING_STYLE, children: [
          /* @__PURE__ */ jsxRuntimeExports.jsx("span", { style: GRAPH_CONTROL_HEADING_TEXT_STYLE, children: heading }),
          /* @__PURE__ */ jsxRuntimeExports.jsx(
            "button",
            {
              type: "button",
              "aria-expanded": isExpanded,
              "aria-controls": isExpanded ? resolvedContentId : void 0,
              "aria-label": isExpanded ? `Collapse control details${disclosureControlLabelSuffix}` : `Expand control details${disclosureControlLabelSuffix}`,
              style: disclosureButtonStyle,
              onClick: onToggleExpanded,
              onFocus: () => setIsHeadingButtonFocused(true),
              onBlur: () => {
                setIsHeadingButtonFocused(false);
                setIsHeadingButtonPressed(false);
              },
              onMouseEnter: () => setIsHeadingButtonHovered(true),
              onMouseLeave: () => {
                setIsHeadingButtonHovered(false);
                setIsHeadingButtonPressed(false);
              },
              onMouseDown: () => setIsHeadingButtonPressed(true),
              onMouseUp: () => setIsHeadingButtonPressed(false),
              onKeyDown: (event) => {
                if (!event.repeat && (event.key === "Enter" || event.key === " ")) {
                  setIsHeadingButtonPressed(true);
                }
              },
              onKeyUp: (event) => {
                if (event.key === "Enter" || event.key === " ") {
                  setIsHeadingButtonPressed(false);
                }
              },
              children: /* @__PURE__ */ jsxRuntimeExports.jsx("span", { "aria-hidden": "true", style: disclosureIconStyle })
            }
          )
        ] }),
        isExpanded ? /* @__PURE__ */ jsxRuntimeExports.jsx(
          "div",
          {
            id: resolvedContentId,
            role: "region",
            "aria-labelledby": headingId,
            style: GRAPH_CONTROL_CONTENT_STYLE,
            children
          }
        ) : null
      ]
    }
  );
}
function ControlsStack({
  id,
  ariaLabel = "Graph controls",
  children
}) {
  return /* @__PURE__ */ jsxRuntimeExports.jsx("div", { id, style: { marginTop: "12px", marginBottom: "12px" }, children: /* @__PURE__ */ jsxRuntimeExports.jsx(
    "div",
    {
      role: "group",
      "aria-label": ariaLabel,
      style: {
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        width: "100%",
        minWidth: 0,
        overflowX: "hidden"
      },
      children
    }
  ) });
}
function clearKeyFromRecord(previousRecord, key) {
  if (!Object.prototype.hasOwnProperty.call(previousRecord, key)) {
    return previousRecord;
  }
  const nextRecord = { ...previousRecord };
  delete nextRecord[key];
  return nextRecord;
}
function useControlInputState() {
  const [draftByKey, setDraftByKey] = useState({});
  const [errorByKey, setErrorByKey] = useState({});
  const draftByKeyRef = useRef({});
  const committingInputKeysRef = useRef(/* @__PURE__ */ new Set());
  function setDraft(key, value) {
    draftByKeyRef.current = {
      ...draftByKeyRef.current,
      [key]: value
    };
    setDraftByKey((previousDraftByKey) => ({
      ...previousDraftByKey,
      [key]: value
    }));
    setErrorByKey(
      (previousErrorByKey) => clearKeyFromRecord(previousErrorByKey, key)
    );
  }
  function setError(key, error) {
    setErrorByKey((previousErrorByKey) => {
      if (error) {
        if (previousErrorByKey[key] === error) {
          return previousErrorByKey;
        }
        return { ...previousErrorByKey, [key]: error };
      }
      return clearKeyFromRecord(previousErrorByKey, key);
    });
  }
  function clearDraft(key) {
    draftByKeyRef.current = clearKeyFromRecord(draftByKeyRef.current, key);
    setDraftByKey(
      (previousDraftByKey) => clearKeyFromRecord(previousDraftByKey, key)
    );
  }
  function hasDraft(key) {
    return Object.prototype.hasOwnProperty.call(draftByKeyRef.current, key);
  }
  function isCommitting(key) {
    return committingInputKeysRef.current.has(key);
  }
  function pruneToActiveKeys(activeKeys) {
    setDraftByKey((previousDraftByKey) => {
      const nextDraftByKey = {};
      let changed = false;
      for (const key in previousDraftByKey) {
        if (Object.prototype.hasOwnProperty.call(
          previousDraftByKey,
          key
        )) {
          if (activeKeys.has(key)) {
            nextDraftByKey[key] = previousDraftByKey[key];
          } else {
            changed = true;
          }
        }
      }
      if (changed) {
        draftByKeyRef.current = nextDraftByKey;
        return nextDraftByKey;
      }
      draftByKeyRef.current = previousDraftByKey;
      return previousDraftByKey;
    });
    setErrorByKey((previousErrorByKey) => {
      const nextErrorByKey = {};
      let changed = false;
      for (const key in previousErrorByKey) {
        if (Object.prototype.hasOwnProperty.call(
          previousErrorByKey,
          key
        )) {
          if (activeKeys.has(key)) {
            nextErrorByKey[key] = previousErrorByKey[key];
          } else {
            changed = true;
          }
        }
      }
      return changed ? nextErrorByKey : previousErrorByKey;
    });
  }
  async function commitParsedInput({
    key,
    rawValue,
    parse,
    errorMessage,
    onParsed,
    isUnchanged
  }) {
    if (isCommitting(key)) {
      return;
    }
    committingInputKeysRef.current.add(key);
    try {
      const parsed = parse(rawValue);
      if (parsed === null) {
        setError(key, errorMessage);
        return;
      }
      if (isUnchanged?.(parsed)) {
        setError(key, null);
        clearDraft(key);
        return;
      }
      setError(key, null);
      clearDraft(key);
      await onParsed(parsed);
    } finally {
      committingInputKeysRef.current.delete(key);
    }
  }
  return {
    draftByKey,
    errorByKey,
    setDraft,
    setError,
    clearDraft,
    hasDraft,
    isCommitting,
    pruneToActiveKeys,
    commitParsedInput
  };
}
function useLatestValues(valuesByKey) {
  const latestValuesRef = useRef({});
  useEffect(() => {
    latestValuesRef.current = {
      ...latestValuesRef.current,
      ...valuesByKey
    };
    const activeKeys = new Set(Object.keys(valuesByKey));
    for (const key of Object.keys(latestValuesRef.current)) {
      if (!activeKeys.has(key)) {
        delete latestValuesRef.current[key];
      }
    }
  }, [valuesByKey]);
  function getLatestValue(key, fallback) {
    return latestValuesRef.current[key] ?? fallback;
  }
  function setLatestValue(key, value) {
    latestValuesRef.current = {
      ...latestValuesRef.current,
      [key]: value
    };
  }
  return {
    getLatestValue,
    setLatestValue
  };
}
function useTransientKeys() {
  const [transientKeySet, setTransientKeySet] = useState(
    () => /* @__PURE__ */ new Set()
  );
  function markTransient(key) {
    setTransientKeySet((previousTransientKeySet) => {
      if (previousTransientKeySet.has(key)) {
        return previousTransientKeySet;
      }
      const nextTransientKeySet = new Set(previousTransientKeySet);
      nextTransientKeySet.add(key);
      return nextTransientKeySet;
    });
  }
  function clearTransient(key) {
    setTransientKeySet((previousTransientKeySet) => {
      if (!previousTransientKeySet.has(key)) {
        return previousTransientKeySet;
      }
      const nextTransientKeySet = new Set(previousTransientKeySet);
      nextTransientKeySet.delete(key);
      return nextTransientKeySet;
    });
  }
  function isTransient(key) {
    return transientKeySet.has(key);
  }
  function pruneToActiveKeys(activeKeys) {
    setTransientKeySet((previousTransientKeySet) => {
      const nextTransientKeySet = /* @__PURE__ */ new Set();
      for (const key of previousTransientKeySet) {
        if (activeKeys.has(key)) {
          nextTransientKeySet.add(key);
        }
      }
      if (nextTransientKeySet.size === previousTransientKeySet.size) {
        let changed = false;
        for (const key of nextTransientKeySet) {
          if (!previousTransientKeySet.has(key)) {
            changed = true;
            break;
          }
        }
        if (!changed) {
          return previousTransientKeySet;
        }
      }
      return nextTransientKeySet;
    });
  }
  return {
    markTransient,
    clearTransient,
    isTransient,
    pruneToActiveKeys
  };
}
const DEFAULT_INITIAL_EXPANDED_GRAPH_CONTROL_COUNT = 2;
const GRAPH_CONTROL_TYPES = [
  "point",
  "circle",
  "regularPolygon",
  "polygon",
  "triangle",
  "rectangle",
  "lineSegment",
  "vector"
];
const GRAPH_CONTROL_TYPE_SET = new Set(
  GRAPH_CONTROL_TYPES
);
function selectInitialExpandedGraphControlIds(orderedControls, count = DEFAULT_INITIAL_EXPANDED_GRAPH_CONTROL_COUNT) {
  const normalizedCount = Number.isFinite(count) ? Math.max(0, Math.floor(count)) : 0;
  return new Set(
    orderedControls.slice(0, normalizedCount).map((control) => control.componentIdx)
  );
}
function selectGraphControlsByType(controls, controlType) {
  return controls.filter(
    (item) => item.controlType === controlType
  );
}
function assertKnownGraphControlType(controlType) {
  if (GRAPH_CONTROL_TYPE_SET.has(controlType)) {
    return controlType;
  }
  throw new Error(
    `[graph-controls] Unsupported controlType "${controlType}" in graphicalDescendantsForControls.`
  );
}
function sortGraphControlsForDisplay(controls) {
  if (controls.length <= 1) {
    return controls;
  }
  const controlsByOrder = /* @__PURE__ */ new Map();
  for (const control of controls) {
    const existingGroup = controlsByOrder.get(control.controlOrder);
    if (existingGroup) {
      existingGroup.push(control);
      continue;
    }
    controlsByOrder.set(control.controlOrder, [control]);
  }
  const positiveOrderKeys = Array.from(controlsByOrder.keys()).filter((order) => order > 0).sort((a, b) => a - b);
  const nextIndexByOrder = /* @__PURE__ */ new Map();
  function hasRemaining(order) {
    const group = controlsByOrder.get(order);
    if (!group) {
      return false;
    }
    return (nextIndexByOrder.get(order) ?? 0) < group.length;
  }
  function takeNext(order) {
    const group = controlsByOrder.get(order);
    if (!group) {
      return null;
    }
    const nextIndex = nextIndexByOrder.get(order) ?? 0;
    if (nextIndex >= group.length) {
      return null;
    }
    nextIndexByOrder.set(order, nextIndex + 1);
    return group[nextIndex];
  }
  function selectOrderForSlot(slotNumber) {
    for (const order of positiveOrderKeys) {
      if (order > slotNumber) {
        break;
      }
      if (hasRemaining(order)) {
        return order;
      }
    }
    if (hasRemaining(0)) {
      return 0;
    }
    for (const order of positiveOrderKeys) {
      if (order > slotNumber && hasRemaining(order)) {
        return order;
      }
    }
    return null;
  }
  const result = [];
  for (let slotNumber = 1; result.length < controls.length; slotNumber += 1) {
    const selectedOrder = selectOrderForSlot(slotNumber);
    if (selectedOrder === null) {
      break;
    }
    const nextControl = takeNext(selectedOrder);
    if (nextControl) {
      result.push(nextControl);
    }
  }
  return result;
}
function normalizeGraphControlsMode(value) {
  if (typeof value !== "string") {
    return "none";
  }
  const normalized = value.toLowerCase();
  if (normalized === "all" || normalized === "slidersonly" || normalized === "inputsonly" || normalized === "none") {
    return normalized;
  }
  return "none";
}
function normalizePointControlsMode(value) {
  if (typeof value !== "string") {
    return "both";
  }
  const normalized = value.toLowerCase();
  if (normalized === "both" || normalized === "xonly" || normalized === "yonly" || normalized === "none") {
    return normalized;
  }
  return "both";
}
function normalizeCircleControlsMode(value) {
  if (typeof value !== "string") {
    return "centerandradius";
  }
  const normalized = value.toLowerCase();
  if (normalized === "center" || normalized === "radius" || normalized === "centerandradius" || normalized === "none") {
    return normalized;
  }
  return "centerandradius";
}
function normalizeRegularPolygonControlsMode(value) {
  if (typeof value !== "string") {
    return "centerandradius";
  }
  const normalized = value.toLowerCase();
  if (normalized === "center" || normalized === "radius" || normalized === "centerandradius" || normalized === "none") {
    return normalized;
  }
  return "centerandradius";
}
function normalizePolygonControlsMode(value) {
  if (typeof value !== "string") {
    return "center";
  }
  const normalized = value.toLowerCase();
  if (normalized === "center" || normalized === "none") {
    return normalized;
  }
  return "center";
}
function normalizeTriangleControlsMode(value) {
  if (typeof value !== "string") {
    return "center";
  }
  const normalized = value.toLowerCase();
  if (normalized === "center" || normalized === "none") {
    return normalized;
  }
  return "center";
}
function normalizeRectangleControlsMode(value) {
  if (typeof value !== "string") {
    return "centerwidthandheight";
  }
  const normalized = value.toLowerCase();
  if (normalized === "center" || normalized === "widthandheight" || normalized === "centerwidthandheight" || normalized === "none") {
    return normalized;
  }
  return "centerwidthandheight";
}
function normalizeLineSegmentControlsMode(value) {
  if (typeof value !== "string") {
    return "endpoints";
  }
  const normalized = value.toLowerCase();
  if (normalized === "endpoints" || normalized === "none") {
    return normalized;
  }
  return "endpoints";
}
function normalizeVectorControlsMode(value) {
  if (typeof value !== "string") {
    return "displacement";
  }
  const normalized = value.toLowerCase();
  if (normalized === "displacement" || normalized === "headandtail" || normalized === "headonly" || normalized === "tailonly" || normalized === "none") {
    return normalized;
  }
  return "displacement";
}
function makeInputErrorId(elementId, controlFamily, inputKey) {
  return `${elementId}-error-${controlFamily}-${inputKey.replace(/[|:]/g, "-")}`;
}
function makePointLikeInputKey(componentIdx, pointRole, suffix, kind) {
  return `${componentIdx}:${pointRole}:${suffix}:${kind}`;
}
function makePointLikeTransientAxisKey(componentIdx, pointRole, axis) {
  return `${componentIdx}:${pointRole}:${axis}:transient`;
}
function makePointLikeDraftKey(componentIdx, pointRole, suffix) {
  return makePointLikeInputKey(componentIdx, pointRole, suffix, "draft");
}
function makeScalarDraftKey(componentIdx, scalarRole) {
  return `${componentIdx}:${scalarRole}:value:draft`;
}
function makeScalarTransientKey(componentIdx, scalarRole) {
  return `${componentIdx}:${scalarRole}:value:transient`;
}
function normalizedSliderBounds(rawMin, rawMax) {
  const min = Math.min(rawMin, rawMax);
  const max = Math.max(rawMin, rawMax);
  return { min, max };
}
function parseSingleMathNumber(input) {
  try {
    const expression = Context.fromText(input);
    const value = expression?.evaluate_to_constant?.();
    return Number.isFinite(value) ? value : null;
  } catch (_error) {
    return null;
  }
}
function parseOrderedPair(input) {
  try {
    const expression = Context.fromText(input);
    const tree = expression?.tree;
    if (!Array.isArray(tree) || tree.length !== 3) {
      return null;
    }
    const operator = tree[0];
    if (operator !== "tuple" && operator !== "vector") {
      return null;
    }
    const x = Context.fromAst(tree[1])?.evaluate_to_constant?.();
    const y = Context.fromAst(tree[2])?.evaluate_to_constant?.();
    if (x === null || y === null) {
      return null;
    }
    if (!Number.isFinite(x) || !Number.isFinite(y)) {
      return null;
    }
    return { x, y };
  } catch (_error) {
    return null;
  }
}
function formatCoordinateForControls(value, displaySettings) {
  const rounded = roundForDisplay({
    value,
    dependencyValues: {
      displayDigits: displaySettings.displayDigits,
      displayDecimals: displaySettings.displayDecimals,
      displaySmallAsZero: displaySettings.displaySmallAsZero
    }
  });
  const params = {};
  if (displaySettings.padZeros) {
    if (Number.isFinite(displaySettings.displayDecimals)) {
      params.padToDecimals = displaySettings.displayDecimals;
    }
    if (displaySettings.displayDigits >= 1) {
      params.padToDigits = displaySettings.displayDigits;
    }
  }
  if (displaySettings.avoidScientificNotation) {
    params.avoidScientificNotation = true;
  }
  return rounded.toString(params);
}
const RANGE_INPUT_KEYS = /* @__PURE__ */ new Set([
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "ArrowDown",
  "PageUp",
  "PageDown",
  "Home",
  "End"
]);
function SliderUI({
  id,
  label,
  ariaLabel,
  ariaValueText,
  min,
  max,
  step,
  value,
  onChange,
  onDragEnd
}) {
  const [localValue, setLocalValue] = useState(value);
  const [transient, setTransient] = useState(false);
  const draggingRef = useRef(false);
  const keyboardActiveRef = useRef(false);
  const hasPlainTextLabel = typeof label === "string" || typeof label === "number";
  function extractInputValue(target) {
    return Number(target?.value ?? localValue);
  }
  function endDrag() {
    draggingRef.current = false;
    keyboardActiveRef.current = false;
    setTransient(false);
    onDragEnd();
  }
  useEffect(() => {
    if (!transient) {
      setLocalValue(value);
    }
  }, [value, transient]);
  return /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { style: { width: "100%", minWidth: 0 }, children: [
    hasPlainTextLabel ? /* @__PURE__ */ jsxRuntimeExports.jsx(
      "label",
      {
        htmlFor: id,
        style: {
          display: "block",
          width: "100%",
          minWidth: 0,
          overflowWrap: "anywhere"
        },
        children: label
      }
    ) : /* @__PURE__ */ jsxRuntimeExports.jsx(
      "div",
      {
        style: {
          display: "block",
          width: "100%",
          minWidth: 0,
          overflowWrap: "anywhere"
        },
        children: label
      }
    ),
    /* @__PURE__ */ jsxRuntimeExports.jsx(
      "input",
      {
        id,
        type: "range",
        min,
        max,
        step,
        value: localValue,
        "aria-label": ariaLabel,
        "aria-valuetext": ariaValueText,
        style: { width: "100%" },
        onKeyDown: (e) => {
          if (RANGE_INPUT_KEYS.has(e.key)) {
            keyboardActiveRef.current = true;
            setTransient(true);
          }
        },
        onInput: (e) => {
          const nextValue = extractInputValue(e.target);
          setLocalValue(nextValue);
          onChange(
            nextValue,
            draggingRef.current || keyboardActiveRef.current
          );
        },
        onPointerDown: (e) => {
          try {
            e.currentTarget.setPointerCapture(e.pointerId);
          } catch {
          }
          draggingRef.current = true;
          setTransient(true);
        },
        onPointerUp: (e) => {
          endDrag();
        },
        onPointerCancel: (e) => {
          endDrag();
        },
        onBlur: (e) => {
          if (transient || keyboardActiveRef.current) {
            keyboardActiveRef.current = false;
            endDrag();
          } else {
            draggingRef.current = false;
          }
        }
      }
    )
  ] });
}
function CommitTextInput({
  id,
  value,
  ariaLabel,
  ariaInvalid,
  ariaDescribedBy,
  onChange,
  onCommit,
  commitErrorContext,
  hasDraft = true,
  isCommitting = false
}) {
  const suppressNextBlurCommitRef = React__default.useRef(false);
  function commitValue(rawValue) {
    if (!hasDraft || isCommitting) {
      return;
    }
    onCommit(rawValue).catch((error) => {
      console.error(commitErrorContext, error);
    });
  }
  return /* @__PURE__ */ jsxRuntimeExports.jsx(
    "input",
    {
      id,
      type: "text",
      value,
      "aria-label": ariaLabel,
      "aria-invalid": ariaInvalid ? true : void 0,
      "aria-describedby": ariaDescribedBy,
      style: GRAPH_CONTROL_TEXT_INPUT_STYLE,
      onChange: (event) => {
        suppressNextBlurCommitRef.current = false;
        onChange(event.target.value);
      },
      onBlur: (event) => {
        if (suppressNextBlurCommitRef.current) {
          suppressNextBlurCommitRef.current = false;
          return;
        }
        commitValue(event.target.value);
      },
      onKeyDown: (event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          suppressNextBlurCommitRef.current = true;
          commitValue(event.currentTarget.value);
        }
      }
    }
  );
}
function InputWithError({
  id,
  value,
  ariaLabel,
  error,
  errorId,
  onDraftChange,
  onCommit,
  hasDraft,
  isCommitting,
  commitErrorContext
}) {
  return /* @__PURE__ */ jsxRuntimeExports.jsxs(jsxRuntimeExports.Fragment, { children: [
    /* @__PURE__ */ jsxRuntimeExports.jsx(
      CommitTextInput,
      {
        id,
        value,
        ariaLabel,
        ariaInvalid: Boolean(error),
        ariaDescribedBy: error ? errorId : void 0,
        onChange: onDraftChange,
        onCommit,
        hasDraft,
        isCommitting,
        commitErrorContext
      }
    ),
    error ? /* @__PURE__ */ jsxRuntimeExports.jsx("span", { id: errorId, style: GRAPH_CONTROL_ERROR_TEXT_STYLE, children: error }) : null
  ] });
}
function ControlSectionHeading({
  heading,
  withDivider = true
}) {
  if (!heading) {
    return null;
  }
  return /* @__PURE__ */ jsxRuntimeExports.jsx(
    "h4",
    {
      style: withDivider ? GRAPH_CONTROL_SECTION_HEADING_WITH_DIVIDER_STYLE : GRAPH_CONTROL_SECTION_HEADING_STYLE,
      children: heading
    }
  );
}
function PointControlView({
  id,
  controlId,
  sectionHeading,
  sectionHeadingHasDivider = true,
  labelForAria,
  graphControlsMode,
  controlsMode,
  pairInput,
  axisControls
}) {
  const includeSliders = graphControlsMode === "all" || graphControlsMode === "slidersonly";
  const includeInputs = graphControlsMode === "all" || graphControlsMode === "inputsonly";
  const showXAxis = controlsMode !== "yonly";
  const showYAxis = controlsMode !== "xonly";
  const showAxisInputsInline = graphControlsMode === "all";
  const showInputsOnly = graphControlsMode === "inputsonly" && includeInputs;
  function renderLabeledInput({
    inputId,
    label,
    input
  }) {
    return /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { style: GRAPH_CONTROL_INPUT_BLOCK_STYLE, children: [
      /* @__PURE__ */ jsxRuntimeExports.jsx("label", { htmlFor: inputId, children: label }),
      /* @__PURE__ */ jsxRuntimeExports.jsx(
        InputWithError,
        {
          id: inputId,
          value: input.value,
          ariaLabel: input.ariaLabel,
          error: input.error,
          errorId: input.errorId,
          onDraftChange: input.onDraftChange,
          onCommit: input.onCommit,
          hasDraft: input.hasDraft,
          isCommitting: input.isCommitting,
          commitErrorContext: input.commitErrorContext
        }
      )
    ] });
  }
  function renderAxisSlider(axis) {
    const axisControl = axisControls[axis];
    return /* @__PURE__ */ jsxRuntimeExports.jsx(
      SliderUI,
      {
        id: `${id}-${controlId}-${axis}`,
        label: showAxisInputsInline ? /* @__PURE__ */ jsxRuntimeExports.jsxs("span", { style: GRAPH_CONTROL_INLINE_INPUT_STACK_STYLE, children: [
          axisControl.label,
          ":",
          " ",
          /* @__PURE__ */ jsxRuntimeExports.jsx(
            InputWithError,
            {
              value: axisControl.input.value,
              ariaLabel: axisControl.input.ariaLabel,
              error: axisControl.input.error,
              errorId: axisControl.input.errorId,
              onDraftChange: axisControl.input.onDraftChange,
              onCommit: axisControl.input.onCommit,
              hasDraft: axisControl.input.hasDraft,
              isCommitting: axisControl.input.isCommitting,
              commitErrorContext: axisControl.input.commitErrorContext
            }
          )
        ] }) : `${axisControl.label}: ${axisControl.displayValue}`,
        ariaLabel: axisControl.sliderAriaLabel,
        ariaValueText: axisControl.sliderAriaValueText,
        min: axisControl.min,
        max: axisControl.max,
        step: axisControl.step,
        value: axisControl.value,
        onChange: axisControl.onSliderChange,
        onDragEnd: axisControl.onDragEnd
      }
    );
  }
  return /* @__PURE__ */ jsxRuntimeExports.jsxs(jsxRuntimeExports.Fragment, { children: [
    /* @__PURE__ */ jsxRuntimeExports.jsx(
      ControlSectionHeading,
      {
        heading: sectionHeading,
        withDivider: sectionHeadingHasDivider
      }
    ),
    showInputsOnly && controlsMode === "both" ? /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { style: GRAPH_CONTROL_INPUT_BLOCK_STYLE, children: [
      /* @__PURE__ */ jsxRuntimeExports.jsx("label", { htmlFor: `${id}-${controlId}-pair`, children: "Coordinates" }),
      /* @__PURE__ */ jsxRuntimeExports.jsx(
        InputWithError,
        {
          id: `${id}-${controlId}-pair`,
          value: pairInput.value,
          ariaLabel: pairInput.ariaLabel,
          error: pairInput.error,
          errorId: pairInput.errorId,
          onDraftChange: pairInput.onDraftChange,
          onCommit: pairInput.onCommit,
          hasDraft: pairInput.hasDraft,
          isCommitting: pairInput.isCommitting,
          commitErrorContext: pairInput.commitErrorContext
        }
      )
    ] }) : null,
    showInputsOnly && controlsMode === "xonly" ? renderLabeledInput({
      inputId: `${id}-${controlId}-x-input`,
      label: "x",
      input: {
        ...axisControls.x.input,
        ariaLabel: axisControls.x.inputOnlyAriaLabel ?? `x input for ${labelForAria}`
      }
    }) : null,
    showInputsOnly && controlsMode === "yonly" ? renderLabeledInput({
      inputId: `${id}-${controlId}-y-input`,
      label: "y",
      input: {
        ...axisControls.y.input,
        ariaLabel: axisControls.y.inputOnlyAriaLabel ?? `y input for ${labelForAria}`
      }
    }) : null,
    includeSliders && showXAxis ? renderAxisSlider("x") : null,
    includeSliders && showYAxis ? renderAxisSlider("y") : null
  ] });
}
function PointControlCoordinator({
  id,
  controlId,
  componentIdx,
  pointRole,
  sectionHeading,
  sectionHeadingHasDivider = true,
  labelForAria,
  xAxisLabel = "x",
  yAxisLabel = "y",
  pairAriaLabel,
  xSliderAriaLabel,
  ySliderAriaLabel,
  xInputAriaLabel,
  yInputAriaLabel,
  graphControlsMode,
  pointControlsMode,
  x,
  y,
  xMin,
  xMax,
  yMin,
  yMax,
  formatCoordinate,
  onMovePointLike
}) {
  const {
    draftByKey,
    errorByKey,
    setDraft,
    hasDraft,
    isCommitting,
    pruneToActiveKeys: pruneInputKeysToActive,
    commitParsedInput
  } = useControlInputState();
  const {
    isTransient,
    markTransient,
    clearTransient,
    pruneToActiveKeys: pruneTransientKeysToActive
  } = useTransientKeys();
  const [rendererSliderCoordinates, setRendererSliderCoordinates] = useState({
    x,
    y
  });
  const latestPointValuesByKey = useMemo(() => {
    return {
      point: { x, y }
    };
  }, [x, y]);
  const { getLatestValue, setLatestValue } = useLatestValues(
    latestPointValuesByKey
  );
  useEffect(() => {
    const activeTransientKeys = /* @__PURE__ */ new Set();
    if (pointControlsMode !== "yonly" && pointControlsMode !== "none") {
      activeTransientKeys.add(
        makePointLikeTransientAxisKey(componentIdx, pointRole, "x")
      );
    }
    if (pointControlsMode !== "xonly" && pointControlsMode !== "none") {
      activeTransientKeys.add(
        makePointLikeTransientAxisKey(componentIdx, pointRole, "y")
      );
    }
    pruneTransientKeysToActive(activeTransientKeys);
    const latestCoordinates = getLatestValue("point", { x, y });
    const xIsTransient = isTransient(
      makePointLikeTransientAxisKey(componentIdx, pointRole, "x")
    );
    const yIsTransient = isTransient(
      makePointLikeTransientAxisKey(componentIdx, pointRole, "y")
    );
    setRendererSliderCoordinates((previousCoordinates) => {
      const nextCoordinates = {
        x: xIsTransient ? previousCoordinates.x ?? latestCoordinates.x : x,
        y: yIsTransient ? previousCoordinates.y ?? latestCoordinates.y : y
      };
      if (nextCoordinates.x === previousCoordinates.x && nextCoordinates.y === previousCoordinates.y) {
        return previousCoordinates;
      }
      return nextCoordinates;
    });
  }, [
    componentIdx,
    getLatestValue,
    isTransient,
    pointControlsMode,
    pointRole,
    pruneTransientKeysToActive,
    x,
    y
  ]);
  const xInputKey = makePointLikeDraftKey(componentIdx, pointRole, "x");
  const yInputKey = makePointLikeDraftKey(componentIdx, pointRole, "y");
  const pairInputKey = makePointLikeDraftKey(componentIdx, pointRole, "pair");
  useEffect(() => {
    const activeInputKeys = /* @__PURE__ */ new Set();
    if (graphControlsMode === "all") {
      if (pointControlsMode !== "yonly" && pointControlsMode !== "none") {
        activeInputKeys.add(xInputKey);
      }
      if (pointControlsMode !== "xonly" && pointControlsMode !== "none") {
        activeInputKeys.add(yInputKey);
      }
    } else if (graphControlsMode === "inputsonly") {
      if (pointControlsMode === "both") {
        activeInputKeys.add(pairInputKey);
      } else if (pointControlsMode === "xonly") {
        activeInputKeys.add(xInputKey);
      } else if (pointControlsMode === "yonly") {
        activeInputKeys.add(yInputKey);
      }
    }
    pruneInputKeysToActive(activeInputKeys);
  }, [
    graphControlsMode,
    pairInputKey,
    pointControlsMode,
    pruneInputKeysToActive,
    xInputKey,
    yInputKey
  ]);
  const formattedX = formatCoordinate(rendererSliderCoordinates.x);
  const formattedY = formatCoordinate(rendererSliderCoordinates.y);
  const { min: normalizedXMin, max: normalizedXMax } = normalizedSliderBounds(
    xMin,
    xMax
  );
  const { min: normalizedYMin, max: normalizedYMax } = normalizedSliderBounds(
    yMin,
    yMax
  );
  const xStep = normalizedXMax !== normalizedXMin ? (normalizedXMax - normalizedXMin) / 100 : 1;
  const yStep = normalizedYMax !== normalizedYMin ? (normalizedYMax - normalizedYMin) / 100 : 1;
  async function movePointLike({
    nextX,
    nextY,
    transient
  }) {
    await onMovePointLike({
      componentIdx,
      pointRole,
      x: nextX,
      y: nextY,
      transient,
      skippable: transient
    });
  }
  async function updatePointCoordinateFromSlider({
    axis,
    value,
    transient
  }) {
    const currentCoordinates = getLatestValue("point", {
      x,
      y
    });
    const nextCoordinates = {
      x: axis === "x" ? value : currentCoordinates.x,
      y: axis === "y" ? value : currentCoordinates.y
    };
    setLatestValue("point", nextCoordinates);
    setRendererSliderCoordinates(nextCoordinates);
    if (transient) {
      markTransient(
        makePointLikeTransientAxisKey(componentIdx, pointRole, axis)
      );
    }
    await movePointLike({
      nextX: nextCoordinates.x,
      nextY: nextCoordinates.y,
      transient
    });
  }
  function clearTransientAxis(axis) {
    clearTransient(
      makePointLikeTransientAxisKey(componentIdx, pointRole, axis)
    );
  }
  async function commitAxisInput({
    key,
    axis,
    rawValue
  }) {
    const currentCoordinates = getLatestValue("point", {
      x,
      y
    });
    const currentValue = axis === "x" ? currentCoordinates.x : currentCoordinates.y;
    await commitParsedInput({
      key,
      rawValue,
      parse: parseSingleMathNumber,
      errorMessage: "Enter a valid number or numeric expression.",
      isUnchanged: (value) => value === currentValue,
      onParsed: async (value) => {
        const nextCoordinates = {
          x: axis === "x" ? value : currentCoordinates.x,
          y: axis === "y" ? value : currentCoordinates.y
        };
        setLatestValue("point", nextCoordinates);
        setRendererSliderCoordinates(nextCoordinates);
        await movePointLike({
          nextX: nextCoordinates.x,
          nextY: nextCoordinates.y,
          transient: false
        });
      }
    });
  }
  async function commitPairInput(rawValue) {
    const currentCoordinates = getLatestValue("point", {
      x,
      y
    });
    await commitParsedInput({
      key: pairInputKey,
      rawValue,
      parse: parseOrderedPair,
      errorMessage: "Enter an ordered pair like (x,y) with numeric values.",
      isUnchanged: (value) => {
        return value.x === currentCoordinates.x && value.y === currentCoordinates.y;
      },
      onParsed: async (value) => {
        setLatestValue("point", value);
        setRendererSliderCoordinates(value);
        await movePointLike({
          nextX: value.x,
          nextY: value.y,
          transient: false
        });
      }
    });
  }
  return /* @__PURE__ */ jsxRuntimeExports.jsx(
    PointControlView,
    {
      id,
      controlId,
      sectionHeading,
      sectionHeadingHasDivider,
      labelForAria,
      graphControlsMode,
      controlsMode: pointControlsMode,
      pairInput: {
        value: draftByKey[pairInputKey] ?? `(${formattedX},${formattedY})`,
        ariaLabel: pairAriaLabel ?? `coordinates for ${labelForAria}`,
        error: errorByKey[pairInputKey],
        errorId: makeInputErrorId(id, "point", pairInputKey),
        onDraftChange: (value) => {
          setDraft(pairInputKey, value);
        },
        onCommit: commitPairInput,
        hasDraft: hasDraft(pairInputKey),
        isCommitting: isCommitting(pairInputKey),
        commitErrorContext: `[graph-controls] failed to commit ${pairInputKey} input`
      },
      axisControls: {
        x: {
          label: xAxisLabel,
          sliderAriaLabel: xSliderAriaLabel ?? `x coordinate for ${labelForAria}`,
          sliderAriaValueText: formattedX,
          inputOnlyAriaLabel: xInputAriaLabel ?? `x input for ${labelForAria}`,
          displayValue: formattedX,
          min: normalizedXMin,
          max: normalizedXMax,
          step: xStep,
          value: rendererSliderCoordinates.x,
          onSliderChange: (value, transient) => {
            updatePointCoordinateFromSlider({
              axis: "x",
              value,
              transient
            }).catch((error) => {
              console.error(
                `[graph-controls] failed to update x slider for ${componentIdx}:${pointRole}`,
                error
              );
            });
          },
          onDragEnd: () => {
            clearTransientAxis("x");
          },
          input: {
            value: draftByKey[xInputKey] ?? formattedX,
            ariaLabel: xInputAriaLabel ?? `x value input for ${labelForAria}`,
            error: errorByKey[xInputKey],
            errorId: makeInputErrorId(id, "point", xInputKey),
            onDraftChange: (value) => {
              setDraft(xInputKey, value);
            },
            onCommit: async (rawValue) => {
              await commitAxisInput({
                key: xInputKey,
                axis: "x",
                rawValue
              });
            },
            hasDraft: hasDraft(xInputKey),
            isCommitting: isCommitting(xInputKey),
            commitErrorContext: `[graph-controls] failed to commit ${xInputKey} input`
          }
        },
        y: {
          label: yAxisLabel,
          sliderAriaLabel: ySliderAriaLabel ?? `y coordinate for ${labelForAria}`,
          sliderAriaValueText: formattedY,
          inputOnlyAriaLabel: yInputAriaLabel ?? `y input for ${labelForAria}`,
          displayValue: formattedY,
          min: normalizedYMin,
          max: normalizedYMax,
          step: yStep,
          value: rendererSliderCoordinates.y,
          onSliderChange: (value, transient) => {
            updatePointCoordinateFromSlider({
              axis: "y",
              value,
              transient
            }).catch((error) => {
              console.error(
                `[graph-controls] failed to update y slider for ${componentIdx}:${pointRole}`,
                error
              );
            });
          },
          onDragEnd: () => {
            clearTransientAxis("y");
          },
          input: {
            value: draftByKey[yInputKey] ?? formattedY,
            ariaLabel: yInputAriaLabel ?? `y value input for ${labelForAria}`,
            error: errorByKey[yInputKey],
            errorId: makeInputErrorId(id, "point", yInputKey),
            onDraftChange: (value) => {
              setDraft(yInputKey, value);
            },
            onCommit: async (rawValue) => {
              await commitAxisInput({
                key: yInputKey,
                axis: "y",
                rawValue
              });
            },
            hasDraft: hasDraft(yInputKey),
            isCommitting: isCommitting(yInputKey),
            commitErrorContext: `[graph-controls] failed to commit ${yInputKey} input`
          }
        }
      }
    }
  );
}
function getPointSections(mode) {
  if (mode === "none") {
    return [];
  }
  return [{ controlIdSuffix: "point", pointControlsMode: mode }];
}
const PointControlsFamily = React__default.memo(function PointControlsFamily2({
  id,
  SVs,
  callAction,
  isGraphControlExpanded,
  toggleGraphControlExpanded
}) {
  const points = selectGraphControlsByType(
    SVs.graphicalDescendantsForControls,
    "point"
  );
  const graphControlsMode = normalizeGraphControlsMode(SVs.addControls);
  if (graphControlsMode === "none" || points.length === 0) {
    return null;
  }
  async function movePointLike({
    componentIdx,
    x,
    y,
    transient,
    skippable
  }) {
    try {
      await callAction({
        action: { actionName: "movePoint", componentIdx },
        args: {
          x,
          y,
          pointRole: "point",
          transient,
          skippable
        }
      });
    } catch (error) {
      console.error(
        `[graph-controls] movePoint failed for component ${componentIdx}`,
        error
      );
    }
  }
  const controlsSection = points.map((point) => {
    const pointControlsMode = normalizePointControlsMode(
      point.addControls
    );
    const sections = getPointSections(pointControlsMode);
    if (sections.length === 0) {
      return null;
    }
    const pointFallbackLabel = `Point ${point.pointNumber}`;
    const pointLabelForAria = accessibleLabelText({
      label: point.label,
      labelHasLatex: point.labelHasLatex,
      fallback: pointFallbackLabel
    });
    const pointLabelForDisplay = point.label.trim() ? renderLabelWithLatex({
      label: point.label,
      labelHasLatex: point.labelHasLatex
    }) : pointFallbackLabel;
    return /* @__PURE__ */ jsxRuntimeExports.jsx(
      ControlCard,
      {
        id: `${id}-point-${point.componentIdx}`,
        headingId: `${id}-point-${point.componentIdx}-heading`,
        contentId: `${id}-point-${point.componentIdx}-content`,
        heading: pointLabelForDisplay,
        disclosureControlLabel: pointLabelForAria,
        isExpanded: isGraphControlExpanded(point.componentIdx),
        onToggleExpanded: () => toggleGraphControlExpanded(point.componentIdx),
        children: sections.map((section) => /* @__PURE__ */ jsxRuntimeExports.jsx(
          PointControlCoordinator,
          {
            id,
            controlId: `${section.controlIdSuffix}-${point.componentIdx}`,
            componentIdx: point.componentIdx,
            pointRole: "point",
            labelForAria: pointLabelForAria,
            graphControlsMode,
            pointControlsMode: section.pointControlsMode,
            x: point.x,
            y: point.y,
            xMin: SVs.xMin,
            xMax: SVs.xMax,
            yMin: SVs.yMin,
            yMax: SVs.yMax,
            formatCoordinate: (value) => formatCoordinateForControls(value, point),
            onMovePointLike: movePointLike
          },
          `${point.componentIdx}-${section.controlIdSuffix}`
        ))
      },
      point.componentIdx
    );
  }).filter((section) => Boolean(section));
  if (controlsSection.length === 0) {
    return null;
  }
  return /* @__PURE__ */ jsxRuntimeExports.jsx(ControlsStack, { id: `${id}-points`, ariaLabel: "Point controls", children: controlsSection });
});
function ScalarControl({
  sectionHeading,
  sectionHeadingHasDivider = true,
  label,
  graphControlsMode,
  input,
  slider
}) {
  const includeSliders = graphControlsMode === "all" || graphControlsMode === "slidersonly";
  const includeInputs = graphControlsMode === "all" || graphControlsMode === "inputsonly";
  if (!includeSliders && includeInputs) {
    return /* @__PURE__ */ jsxRuntimeExports.jsxs(jsxRuntimeExports.Fragment, { children: [
      /* @__PURE__ */ jsxRuntimeExports.jsx(
        ControlSectionHeading,
        {
          heading: sectionHeading,
          withDivider: sectionHeadingHasDivider
        }
      ),
      /* @__PURE__ */ jsxRuntimeExports.jsx("div", { style: GRAPH_CONTROL_INPUT_BLOCK_STYLE, children: /* @__PURE__ */ jsxRuntimeExports.jsxs("label", { children: [
        label,
        /* @__PURE__ */ jsxRuntimeExports.jsx(
          InputWithError,
          {
            value: input.value,
            ariaLabel: input.ariaLabel,
            error: input.error,
            errorId: input.errorId,
            onDraftChange: input.onDraftChange,
            onCommit: input.onCommit,
            hasDraft: input.hasDraft,
            isCommitting: input.isCommitting,
            commitErrorContext: input.commitErrorContext
          }
        )
      ] }) })
    ] });
  }
  if (!includeSliders) {
    return null;
  }
  return /* @__PURE__ */ jsxRuntimeExports.jsxs(jsxRuntimeExports.Fragment, { children: [
    /* @__PURE__ */ jsxRuntimeExports.jsx(
      ControlSectionHeading,
      {
        heading: sectionHeading,
        withDivider: sectionHeadingHasDivider
      }
    ),
    /* @__PURE__ */ jsxRuntimeExports.jsx(
      SliderUI,
      {
        id: slider.id,
        label: graphControlsMode === "all" ? /* @__PURE__ */ jsxRuntimeExports.jsxs("span", { style: GRAPH_CONTROL_INLINE_INPUT_STACK_STYLE, children: [
          label,
          ":",
          /* @__PURE__ */ jsxRuntimeExports.jsx(
            InputWithError,
            {
              value: input.value,
              ariaLabel: input.ariaLabel,
              error: input.error,
              errorId: input.errorId,
              onDraftChange: input.onDraftChange,
              onCommit: input.onCommit,
              hasDraft: input.hasDraft,
              isCommitting: input.isCommitting,
              commitErrorContext: input.commitErrorContext
            }
          )
        ] }) : `${label}: ${slider.displayValue}`,
        ariaLabel: slider.ariaLabel,
        ariaValueText: slider.ariaValueText,
        min: slider.min,
        max: slider.max,
        step: slider.step,
        value: slider.value,
        onChange: slider.onSliderChange,
        onDragEnd: slider.onSliderDragEnd
      }
    )
  ] });
}
function ScalarControlCoordinator({
  id,
  controlId,
  componentIdx,
  scalarRole,
  sectionHeading,
  sectionHeadingHasDivider = true,
  label,
  graphControlsMode,
  value,
  min,
  max,
  step,
  formatValue,
  parseValue,
  controlFamily,
  sliderAriaLabel,
  inputAriaLabel,
  commitErrorContext,
  onUpdateScalar
}) {
  const {
    draftByKey,
    errorByKey,
    setDraft,
    hasDraft,
    isCommitting,
    pruneToActiveKeys: pruneInputKeysToActive,
    commitParsedInput
  } = useControlInputState();
  const {
    isTransient,
    markTransient,
    clearTransient,
    pruneToActiveKeys: pruneTransientKeysToActive
  } = useTransientKeys();
  const [rendererSliderValue, setRendererSliderValue] = useState(value);
  const latestScalarValuesByKey = useMemo(() => ({ scalar: value }), [value]);
  const { getLatestValue, setLatestValue } = useLatestValues(
    latestScalarValuesByKey
  );
  const draftKey = makeScalarDraftKey(componentIdx, scalarRole);
  const transientKey = makeScalarTransientKey(componentIdx, scalarRole);
  useEffect(() => {
    const activeInputKeys = graphControlsMode === "all" || graphControlsMode === "inputsonly" ? /* @__PURE__ */ new Set([draftKey]) : /* @__PURE__ */ new Set();
    pruneInputKeysToActive(activeInputKeys);
  }, [draftKey, graphControlsMode, pruneInputKeysToActive]);
  useEffect(() => {
    pruneTransientKeysToActive(/* @__PURE__ */ new Set([transientKey]));
    const scalarIsTransient = isTransient(transientKey);
    setRendererSliderValue((previousValue) => {
      const nextValue = scalarIsTransient ? previousValue : value;
      return nextValue === previousValue ? previousValue : nextValue;
    });
  }, [isTransient, pruneTransientKeysToActive, transientKey, value]);
  async function updateScalarFromSlider({
    nextValue,
    transient
  }) {
    setLatestValue("scalar", nextValue);
    setRendererSliderValue(nextValue);
    if (transient) {
      markTransient(transientKey);
    }
    await onUpdateScalar({
      componentIdx,
      scalarRole,
      value: nextValue,
      transient,
      skippable: transient
    });
  }
  function clearTransientScalar() {
    clearTransient(transientKey);
  }
  async function commitScalarInput(rawValue) {
    const currentValue = getLatestValue("scalar", value);
    await commitParsedInput({
      key: draftKey,
      rawValue,
      parse: parseValue,
      errorMessage: "Enter a valid number or numeric expression.",
      isUnchanged: (parsedValue) => parsedValue === currentValue,
      onParsed: async (parsedValue) => {
        setLatestValue("scalar", parsedValue);
        setRendererSliderValue(parsedValue);
        await onUpdateScalar({
          componentIdx,
          scalarRole,
          value: parsedValue,
          transient: false,
          skippable: false
        });
      }
    });
  }
  return /* @__PURE__ */ jsxRuntimeExports.jsx(
    ScalarControl,
    {
      sectionHeading,
      sectionHeadingHasDivider,
      label,
      graphControlsMode,
      input: {
        value: draftByKey[draftKey] ?? formatValue(rendererSliderValue),
        ariaLabel: inputAriaLabel,
        error: errorByKey[draftKey],
        errorId: makeInputErrorId(id, controlFamily, draftKey),
        onDraftChange: (draftValue) => {
          setDraft(draftKey, draftValue);
        },
        onCommit: commitScalarInput,
        hasDraft: hasDraft(draftKey),
        isCommitting: isCommitting(draftKey),
        commitErrorContext
      },
      slider: {
        id: `${id}-${controlId}-slider`,
        ariaLabel: sliderAriaLabel,
        ariaValueText: formatValue(rendererSliderValue),
        min,
        max,
        step,
        value: rendererSliderValue,
        displayValue: formatValue(rendererSliderValue),
        onSliderChange: (nextValue, transient) => {
          updateScalarFromSlider({
            nextValue,
            transient
          }).catch((error) => {
            console.error(
              `[graph-controls] failed to update scalar slider for ${componentIdx}:${scalarRole}`,
              error
            );
          });
        },
        onSliderDragEnd: clearTransientScalar
      }
    }
  );
}
function getCircleSections(mode) {
  if (mode === "center") {
    return [{ kind: "center", sectionHeadingHasDivider: false }];
  }
  if (mode === "radius") {
    return [{ kind: "radius", sectionHeadingHasDivider: false }];
  }
  if (mode === "centerandradius") {
    return [
      { kind: "center", sectionHeadingHasDivider: false },
      { kind: "radius", sectionHeadingHasDivider: true }
    ];
  }
  return [];
}
const CircleControlsFamily = React__default.memo(function CircleControlsFamily2({
  id,
  SVs,
  callAction,
  isGraphControlExpanded,
  toggleGraphControlExpanded
}) {
  const graphControlsMode = normalizeGraphControlsMode(SVs.addControls);
  if (graphControlsMode === "none") {
    return null;
  }
  const circles = selectGraphControlsByType(
    SVs.graphicalDescendantsForControls,
    "circle"
  );
  if (circles.length === 0) {
    return null;
  }
  async function moveCircle({
    componentIdx,
    pointRole,
    x,
    y,
    transient,
    skippable
  }) {
    if (pointRole !== "center") {
      return;
    }
    try {
      await callAction({
        action: {
          actionName: "moveCircle",
          componentIdx
        },
        args: {
          center: [x, y],
          pointRole: "center",
          transient,
          skippable
        }
      });
    } catch (error) {
      console.error(
        `[graph-controls] moveCircle failed for component ${componentIdx}`,
        error
      );
    }
  }
  async function changeCircleRadius({
    componentIdx,
    scalarRole,
    value,
    transient,
    skippable
  }) {
    if (scalarRole !== "radius") {
      return;
    }
    const radius = Math.max(0, value);
    try {
      await callAction({
        action: {
          actionName: "changeRadius",
          componentIdx
        },
        args: {
          radius,
          transient,
          skippable
        }
      });
    } catch (error) {
      console.error(
        `[graph-controls] changeRadius failed for component ${componentIdx}`,
        error
      );
    }
  }
  const defaultRadiusMax = Math.max(
    1,
    Math.abs(SVs.xMax - SVs.xMin),
    Math.abs(SVs.yMax - SVs.yMin)
  );
  const cards = circles.map((circle) => {
    const mode = normalizeCircleControlsMode(circle.addControls);
    const sections = getCircleSections(mode);
    if (sections.length === 0) {
      return null;
    }
    const fallbackLabel = `Circle ${circle.circleNumber}`;
    const labelForAria = accessibleLabelText({
      label: circle.label,
      labelHasLatex: circle.labelHasLatex,
      fallback: fallbackLabel
    });
    const labelForDisplay = circle.label.trim() ? renderLabelWithLatex({
      label: circle.label,
      labelHasLatex: circle.labelHasLatex
    }) : fallbackLabel;
    const radiusMax = Math.max(defaultRadiusMax, circle.radius);
    const radiusStep = radiusMax > 0 ? radiusMax / 100 : 1;
    const headingId = `${id}-circle-${circle.componentIdx}-heading`;
    return /* @__PURE__ */ jsxRuntimeExports.jsx(
      ControlCard,
      {
        id: `${id}-circle-${circle.componentIdx}`,
        headingId,
        contentId: `${id}-circle-${circle.componentIdx}-content`,
        heading: labelForDisplay,
        disclosureControlLabel: labelForAria,
        isExpanded: isGraphControlExpanded(circle.componentIdx),
        onToggleExpanded: () => toggleGraphControlExpanded(circle.componentIdx),
        children: sections.map(
          (section) => section.kind === "center" ? /* @__PURE__ */ jsxRuntimeExports.jsx(
            PointControlCoordinator,
            {
              id,
              controlId: `circle-${circle.componentIdx}-center`,
              componentIdx: circle.componentIdx,
              pointRole: "center",
              sectionHeading: "Center",
              sectionHeadingHasDivider: section.sectionHeadingHasDivider,
              labelForAria: `center of ${labelForAria}`,
              pairAriaLabel: `center coordinates for ${labelForAria}`,
              xSliderAriaLabel: `center x coordinate for ${labelForAria}`,
              ySliderAriaLabel: `center y coordinate for ${labelForAria}`,
              xInputAriaLabel: `center x input for ${labelForAria}`,
              yInputAriaLabel: `center y input for ${labelForAria}`,
              graphControlsMode,
              pointControlsMode: "both",
              x: circle.center.x,
              y: circle.center.y,
              xMin: SVs.xMin,
              xMax: SVs.xMax,
              yMin: SVs.yMin,
              yMax: SVs.yMax,
              formatCoordinate: (value) => formatCoordinateForControls(value, circle),
              onMovePointLike: moveCircle
            },
            `${circle.componentIdx}-center`
          ) : /* @__PURE__ */ jsxRuntimeExports.jsx(
            ScalarControlCoordinator,
            {
              id,
              controlId: `circle-${circle.componentIdx}-radius`,
              componentIdx: circle.componentIdx,
              scalarRole: "radius",
              sectionHeading: "Radius",
              sectionHeadingHasDivider: section.sectionHeadingHasDivider,
              label: "radius",
              graphControlsMode,
              value: circle.radius,
              min: 0,
              max: radiusMax,
              step: radiusStep,
              formatValue: (value) => formatCoordinateForControls(value, circle),
              parseValue: parseSingleMathNumber,
              controlFamily: "circle",
              sliderAriaLabel: `radius for ${labelForAria}`,
              inputAriaLabel: `radius input for ${labelForAria}`,
              commitErrorContext: `[graph-controls] failed to commit circle ${circle.componentIdx} radius input`,
              onUpdateScalar: changeCircleRadius
            },
            `${circle.componentIdx}-radius`
          )
        )
      },
      circle.componentIdx
    );
  }).filter((card) => Boolean(card));
  if (cards.length === 0) {
    return null;
  }
  return /* @__PURE__ */ jsxRuntimeExports.jsx(ControlsStack, { id: `${id}-circles`, ariaLabel: "Circle controls", children: cards });
});
const PolygonControlsFamily = React__default.memo(function PolygonControlsFamily2({
  id,
  SVs,
  callAction,
  isGraphControlExpanded,
  toggleGraphControlExpanded
}) {
  const graphControlsMode = normalizeGraphControlsMode(SVs.addControls);
  if (graphControlsMode === "none") {
    return null;
  }
  const polygons = selectGraphControlsByType(
    SVs.graphicalDescendantsForControls,
    "polygon"
  );
  if (polygons.length === 0) {
    return null;
  }
  async function movePolygonCenter({
    componentIdx,
    pointRole,
    x,
    y,
    transient,
    skippable
  }) {
    if (pointRole !== "polygon") {
      return;
    }
    try {
      await callAction({
        action: {
          actionName: "movePolygonCenter",
          componentIdx
        },
        args: {
          center: [x, y],
          pointRole: "polygon",
          transient,
          skippable
        }
      });
    } catch (error) {
      console.error(
        `[graph-controls] movePolygonCenter failed for component ${componentIdx}`,
        error
      );
    }
  }
  const cards = polygons.map((polygon) => {
    const mode = normalizePolygonControlsMode(polygon.addControls);
    if (mode === "none") {
      return null;
    }
    const fallbackLabel = `Polygon ${polygon.polygonNumber}`;
    const labelForAria = accessibleLabelText({
      label: polygon.label,
      labelHasLatex: polygon.labelHasLatex,
      fallback: fallbackLabel
    });
    const labelForDisplay = polygon.label.trim() ? renderLabelWithLatex({
      label: polygon.label,
      labelHasLatex: polygon.labelHasLatex
    }) : fallbackLabel;
    return /* @__PURE__ */ jsxRuntimeExports.jsx(
      ControlCard,
      {
        id: `${id}-polygon-${polygon.componentIdx}`,
        headingId: `${id}-polygon-${polygon.componentIdx}-heading`,
        contentId: `${id}-polygon-${polygon.componentIdx}-content`,
        heading: labelForDisplay,
        disclosureControlLabel: labelForAria,
        isExpanded: isGraphControlExpanded(polygon.componentIdx),
        onToggleExpanded: () => toggleGraphControlExpanded(polygon.componentIdx),
        children: /* @__PURE__ */ jsxRuntimeExports.jsx(
          PointControlCoordinator,
          {
            id,
            controlId: `polygon-${polygon.componentIdx}-center`,
            componentIdx: polygon.componentIdx,
            pointRole: "polygon",
            sectionHeading: "Center",
            sectionHeadingHasDivider: false,
            labelForAria: `center of ${labelForAria}`,
            pairAriaLabel: `center coordinates for ${labelForAria}`,
            xSliderAriaLabel: `x coordinate for center of ${labelForAria}`,
            ySliderAriaLabel: `y coordinate for center of ${labelForAria}`,
            xInputAriaLabel: `center x input for ${labelForAria}`,
            yInputAriaLabel: `center y input for ${labelForAria}`,
            graphControlsMode,
            pointControlsMode: "both",
            x: polygon.center.x,
            y: polygon.center.y,
            xMin: SVs.xMin,
            xMax: SVs.xMax,
            yMin: SVs.yMin,
            yMax: SVs.yMax,
            formatCoordinate: (value) => formatCoordinateForControls(value, polygon),
            onMovePointLike: movePolygonCenter
          }
        )
      },
      polygon.componentIdx
    );
  }).filter((card) => Boolean(card));
  if (cards.length === 0) {
    return null;
  }
  return /* @__PURE__ */ jsxRuntimeExports.jsx(ControlsStack, { id: `${id}-polygons`, ariaLabel: "Polygon controls", children: cards });
});
const TriangleControlsFamily = React__default.memo(function TriangleControlsFamily2({
  id,
  SVs,
  callAction,
  isGraphControlExpanded,
  toggleGraphControlExpanded
}) {
  const graphControlsMode = normalizeGraphControlsMode(SVs.addControls);
  if (graphControlsMode === "none") {
    return null;
  }
  const triangles = selectGraphControlsByType(
    SVs.graphicalDescendantsForControls,
    "triangle"
  );
  if (triangles.length === 0) {
    return null;
  }
  async function moveTriangleCenter({
    componentIdx,
    pointRole,
    x,
    y,
    transient,
    skippable
  }) {
    if (pointRole !== "triangle") {
      return;
    }
    try {
      await callAction({
        action: {
          actionName: "moveTriangleCenter",
          componentIdx
        },
        args: {
          center: [x, y],
          pointRole: "triangle",
          transient,
          skippable
        }
      });
    } catch (error) {
      console.error(
        `[graph-controls] moveTriangleCenter failed for component ${componentIdx}`,
        error
      );
    }
  }
  const cards = triangles.map((triangle) => {
    const mode = normalizeTriangleControlsMode(triangle.addControls);
    if (mode === "none") {
      return null;
    }
    const fallbackLabel = `Triangle ${triangle.triangleNumber}`;
    const labelForAria = accessibleLabelText({
      label: triangle.label,
      labelHasLatex: triangle.labelHasLatex,
      fallback: fallbackLabel
    });
    const labelForDisplay = triangle.label.trim() ? renderLabelWithLatex({
      label: triangle.label,
      labelHasLatex: triangle.labelHasLatex
    }) : fallbackLabel;
    return /* @__PURE__ */ jsxRuntimeExports.jsx(
      ControlCard,
      {
        id: `${id}-triangle-${triangle.componentIdx}`,
        headingId: `${id}-triangle-${triangle.componentIdx}-heading`,
        contentId: `${id}-triangle-${triangle.componentIdx}-content`,
        heading: labelForDisplay,
        disclosureControlLabel: labelForAria,
        isExpanded: isGraphControlExpanded(triangle.componentIdx),
        onToggleExpanded: () => toggleGraphControlExpanded(triangle.componentIdx),
        children: /* @__PURE__ */ jsxRuntimeExports.jsx(
          PointControlCoordinator,
          {
            id,
            controlId: `triangle-${triangle.componentIdx}-center`,
            componentIdx: triangle.componentIdx,
            pointRole: "triangle",
            sectionHeading: "Center",
            sectionHeadingHasDivider: false,
            labelForAria: `center of ${labelForAria}`,
            pairAriaLabel: `center coordinates for ${labelForAria}`,
            xSliderAriaLabel: `x coordinate for center of ${labelForAria}`,
            ySliderAriaLabel: `y coordinate for center of ${labelForAria}`,
            xInputAriaLabel: `center x input for ${labelForAria}`,
            yInputAriaLabel: `center y input for ${labelForAria}`,
            graphControlsMode,
            pointControlsMode: "both",
            x: triangle.center.x,
            y: triangle.center.y,
            xMin: SVs.xMin,
            xMax: SVs.xMax,
            yMin: SVs.yMin,
            yMax: SVs.yMax,
            formatCoordinate: (value) => formatCoordinateForControls(value, triangle),
            onMovePointLike: moveTriangleCenter
          }
        )
      },
      triangle.componentIdx
    );
  }).filter((card) => Boolean(card));
  if (cards.length === 0) {
    return null;
  }
  return /* @__PURE__ */ jsxRuntimeExports.jsx(ControlsStack, { id: `${id}-triangles`, ariaLabel: "Triangle controls", children: cards });
});
function getRegularPolygonSections(mode) {
  if (mode === "center") {
    return [{ kind: "center", sectionHeadingHasDivider: false }];
  }
  if (mode === "radius") {
    return [{ kind: "radius", sectionHeadingHasDivider: false }];
  }
  if (mode === "centerandradius") {
    return [
      { kind: "center", sectionHeadingHasDivider: false },
      { kind: "radius", sectionHeadingHasDivider: true }
    ];
  }
  return [];
}
const RegularPolygonControlsFamily = React__default.memo(function RegularPolygonControlsFamily2({
  id,
  SVs,
  callAction,
  isGraphControlExpanded,
  toggleGraphControlExpanded
}) {
  const graphControlsMode = normalizeGraphControlsMode(SVs.addControls);
  if (graphControlsMode === "none") {
    return null;
  }
  const regularPolygons = selectGraphControlsByType(
    SVs.graphicalDescendantsForControls,
    "regularPolygon"
  );
  if (regularPolygons.length === 0) {
    return null;
  }
  async function moveRegularPolygonCenter({
    componentIdx,
    pointRole,
    x,
    y,
    transient,
    skippable
  }) {
    if (pointRole !== "regularPolygon") {
      return;
    }
    try {
      await callAction({
        action: {
          actionName: "movePolygonCenter",
          componentIdx
        },
        args: {
          center: [x, y],
          pointRole: "regularPolygon",
          transient,
          skippable
        }
      });
    } catch (error) {
      console.error(
        `[graph-controls] movePolygonCenter failed for component ${componentIdx}`,
        error
      );
    }
  }
  async function changeRegularPolygonRadius({
    componentIdx,
    scalarRole,
    value,
    transient,
    skippable
  }) {
    if (scalarRole !== "radius") {
      return;
    }
    const radius = Math.max(0, value);
    try {
      await callAction({
        action: {
          actionName: "changeRadius",
          componentIdx
        },
        args: {
          radius,
          transient,
          skippable
        }
      });
    } catch (error) {
      console.error(
        `[graph-controls] changeRadius failed for component ${componentIdx}`,
        error
      );
    }
  }
  const defaultRadiusMax = Math.max(
    1,
    Math.abs(SVs.xMax - SVs.xMin),
    Math.abs(SVs.yMax - SVs.yMin)
  );
  const cards = regularPolygons.map((regularPolygon) => {
    const mode = normalizeRegularPolygonControlsMode(
      regularPolygon.addControls
    );
    const sections = getRegularPolygonSections(mode);
    if (sections.length === 0) {
      return null;
    }
    const fallbackLabel = `Regular polygon ${regularPolygon.regularPolygonNumber}`;
    const labelForAria = accessibleLabelText({
      label: regularPolygon.label,
      labelHasLatex: regularPolygon.labelHasLatex,
      fallback: fallbackLabel
    });
    const labelForDisplay = regularPolygon.label.trim() ? renderLabelWithLatex({
      label: regularPolygon.label,
      labelHasLatex: regularPolygon.labelHasLatex
    }) : fallbackLabel;
    const radiusMax = Math.max(defaultRadiusMax, regularPolygon.radius);
    const radiusStep = radiusMax > 0 ? radiusMax / 100 : 1;
    return /* @__PURE__ */ jsxRuntimeExports.jsx(
      ControlCard,
      {
        id: `${id}-regularPolygon-${regularPolygon.componentIdx}`,
        headingId: `${id}-regularPolygon-${regularPolygon.componentIdx}-heading`,
        contentId: `${id}-regularPolygon-${regularPolygon.componentIdx}-content`,
        heading: labelForDisplay,
        disclosureControlLabel: labelForAria,
        isExpanded: isGraphControlExpanded(
          regularPolygon.componentIdx
        ),
        onToggleExpanded: () => toggleGraphControlExpanded(regularPolygon.componentIdx),
        children: sections.map(
          (section) => section.kind === "center" ? /* @__PURE__ */ jsxRuntimeExports.jsx(
            PointControlCoordinator,
            {
              id,
              controlId: `regularPolygon-${regularPolygon.componentIdx}-center`,
              componentIdx: regularPolygon.componentIdx,
              pointRole: "regularPolygon",
              sectionHeading: "Center",
              sectionHeadingHasDivider: section.sectionHeadingHasDivider,
              labelForAria: `center of ${labelForAria}`,
              pairAriaLabel: `center coordinates for ${labelForAria}`,
              xSliderAriaLabel: `center x coordinate for ${labelForAria}`,
              ySliderAriaLabel: `center y coordinate for ${labelForAria}`,
              xInputAriaLabel: `center x input for ${labelForAria}`,
              yInputAriaLabel: `center y input for ${labelForAria}`,
              graphControlsMode,
              pointControlsMode: "both",
              x: regularPolygon.center.x,
              y: regularPolygon.center.y,
              xMin: SVs.xMin,
              xMax: SVs.xMax,
              yMin: SVs.yMin,
              yMax: SVs.yMax,
              formatCoordinate: (value) => formatCoordinateForControls(
                value,
                regularPolygon
              ),
              onMovePointLike: moveRegularPolygonCenter
            },
            `${regularPolygon.componentIdx}-center`
          ) : /* @__PURE__ */ jsxRuntimeExports.jsx(
            ScalarControlCoordinator,
            {
              id,
              controlId: `regularPolygon-${regularPolygon.componentIdx}-radius`,
              componentIdx: regularPolygon.componentIdx,
              scalarRole: "radius",
              sectionHeading: "Radius",
              sectionHeadingHasDivider: section.sectionHeadingHasDivider,
              label: "radius",
              graphControlsMode,
              value: regularPolygon.radius,
              min: 0,
              max: radiusMax,
              step: radiusStep,
              formatValue: (value) => formatCoordinateForControls(
                value,
                regularPolygon
              ),
              parseValue: parseSingleMathNumber,
              controlFamily: "regularPolygon",
              sliderAriaLabel: `radius for ${labelForAria}`,
              inputAriaLabel: `radius input for ${labelForAria}`,
              commitErrorContext: `[graph-controls] failed to commit regular polygon ${regularPolygon.componentIdx} radius input`,
              onUpdateScalar: changeRegularPolygonRadius
            },
            `${regularPolygon.componentIdx}-radius`
          )
        )
      },
      regularPolygon.componentIdx
    );
  }).filter((card) => Boolean(card));
  if (cards.length === 0) {
    return null;
  }
  return /* @__PURE__ */ jsxRuntimeExports.jsx(
    ControlsStack,
    {
      id: `${id}-regularPolygons`,
      ariaLabel: "Regular polygon controls",
      children: cards
    }
  );
});
function getRectangleSections(mode) {
  if (mode === "center") {
    return [{ kind: "center", sectionHeadingHasDivider: false }];
  }
  if (mode === "widthandheight") {
    return [
      {
        kind: "width",
        sectionHeading: "Dimensions",
        sectionHeadingHasDivider: false
      },
      {
        kind: "height",
        sectionHeadingHasDivider: false
      }
    ];
  }
  if (mode === "centerwidthandheight") {
    return [
      { kind: "center", sectionHeadingHasDivider: false },
      {
        kind: "width",
        sectionHeading: "Dimensions",
        sectionHeadingHasDivider: true
      },
      {
        kind: "height",
        sectionHeadingHasDivider: false
      }
    ];
  }
  return [];
}
const RectangleControlsFamily = React__default.memo(function RectangleControlsFamily2({
  id,
  SVs,
  callAction,
  isGraphControlExpanded,
  toggleGraphControlExpanded
}) {
  const graphControlsMode = normalizeGraphControlsMode(SVs.addControls);
  if (graphControlsMode === "none") {
    return null;
  }
  const rectangles = selectGraphControlsByType(
    SVs.graphicalDescendantsForControls,
    "rectangle"
  );
  if (rectangles.length === 0) {
    return null;
  }
  async function moveRectangleCenter({
    componentIdx,
    pointRole,
    x,
    y,
    transient,
    skippable
  }) {
    if (pointRole !== "rectangle") {
      return;
    }
    try {
      await callAction({
        action: {
          actionName: "moveRectangleCenter",
          componentIdx
        },
        args: {
          center: [x, y],
          pointRole: "rectangle",
          transient,
          skippable
        }
      });
    } catch (error) {
      console.error(
        `[graph-controls] moveRectangleCenter failed for component ${componentIdx}`,
        error
      );
    }
  }
  async function updateRectangleScalar({
    componentIdx,
    scalarRole,
    value,
    transient,
    skippable
  }) {
    if (scalarRole !== "width" && scalarRole !== "height") {
      return;
    }
    try {
      await callAction({
        action: {
          actionName: scalarRole === "width" ? "changeWidth" : "changeHeight",
          componentIdx
        },
        args: {
          [scalarRole]: Math.max(0, value),
          transient,
          skippable
        }
      });
    } catch (error) {
      console.error(
        `[graph-controls] ${scalarRole} update failed for component ${componentIdx}`,
        error
      );
    }
  }
  const defaultDimensionMax = Math.max(
    1,
    Math.abs(SVs.xMax - SVs.xMin),
    Math.abs(SVs.yMax - SVs.yMin)
  );
  const cards = rectangles.map((rectangle) => {
    const mode = normalizeRectangleControlsMode(rectangle.addControls);
    const sections = getRectangleSections(mode);
    if (sections.length === 0) {
      return null;
    }
    const fallbackLabel = `Rectangle ${rectangle.rectangleNumber}`;
    const labelForAria = accessibleLabelText({
      label: rectangle.label,
      labelHasLatex: rectangle.labelHasLatex,
      fallback: fallbackLabel
    });
    const labelForDisplay = rectangle.label.trim() ? renderLabelWithLatex({
      label: rectangle.label,
      labelHasLatex: rectangle.labelHasLatex
    }) : fallbackLabel;
    const dimensionMax = Math.max(
      defaultDimensionMax,
      rectangle.width,
      rectangle.height
    );
    const dimensionStep = dimensionMax > 0 ? dimensionMax / 100 : 1;
    return /* @__PURE__ */ jsxRuntimeExports.jsx(
      ControlCard,
      {
        id: `${id}-rectangle-${rectangle.componentIdx}`,
        headingId: `${id}-rectangle-${rectangle.componentIdx}-heading`,
        contentId: `${id}-rectangle-${rectangle.componentIdx}-content`,
        heading: labelForDisplay,
        disclosureControlLabel: labelForAria,
        isExpanded: isGraphControlExpanded(rectangle.componentIdx),
        onToggleExpanded: () => toggleGraphControlExpanded(rectangle.componentIdx),
        children: sections.map((section) => {
          if (section.kind === "center") {
            return /* @__PURE__ */ jsxRuntimeExports.jsx(
              PointControlCoordinator,
              {
                id,
                controlId: `rectangle-${rectangle.componentIdx}-center`,
                componentIdx: rectangle.componentIdx,
                pointRole: "rectangle",
                sectionHeading: "Center",
                sectionHeadingHasDivider: section.sectionHeadingHasDivider,
                labelForAria: `center of ${labelForAria}`,
                pairAriaLabel: `center coordinates for ${labelForAria}`,
                xSliderAriaLabel: `center x coordinate for ${labelForAria}`,
                ySliderAriaLabel: `center y coordinate for ${labelForAria}`,
                xInputAriaLabel: `center x input for ${labelForAria}`,
                yInputAriaLabel: `center y input for ${labelForAria}`,
                graphControlsMode,
                pointControlsMode: "both",
                x: rectangle.center.x,
                y: rectangle.center.y,
                xMin: SVs.xMin,
                xMax: SVs.xMax,
                yMin: SVs.yMin,
                yMax: SVs.yMax,
                formatCoordinate: (value) => formatCoordinateForControls(
                  value,
                  rectangle
                ),
                onMovePointLike: moveRectangleCenter
              },
              `${rectangle.componentIdx}-center`
            );
          }
          return /* @__PURE__ */ jsxRuntimeExports.jsx(
            ScalarControlCoordinator,
            {
              id,
              controlId: `rectangle-${rectangle.componentIdx}-${section.kind}`,
              componentIdx: rectangle.componentIdx,
              scalarRole: section.kind,
              sectionHeading: section.sectionHeading,
              sectionHeadingHasDivider: section.sectionHeadingHasDivider,
              label: section.kind,
              graphControlsMode,
              value: rectangle[section.kind],
              min: 0,
              max: dimensionMax,
              step: dimensionStep,
              formatValue: (value) => formatCoordinateForControls(
                value,
                rectangle
              ),
              parseValue: parseSingleMathNumber,
              controlFamily: "rectangle",
              sliderAriaLabel: `${section.kind} for ${labelForAria}`,
              inputAriaLabel: `${section.kind} input for ${labelForAria}`,
              commitErrorContext: `[graph-controls] failed to commit rectangle ${rectangle.componentIdx} ${section.kind} input`,
              onUpdateScalar: updateRectangleScalar
            },
            `${rectangle.componentIdx}-${section.kind}`
          );
        })
      },
      rectangle.componentIdx
    );
  }).filter((card) => Boolean(card));
  if (cards.length === 0) {
    return null;
  }
  return /* @__PURE__ */ jsxRuntimeExports.jsx(ControlsStack, { id: `${id}-rectangles`, ariaLabel: "Rectangle controls", children: cards });
});
function getLineSegmentSections(mode) {
  if (mode !== "endpoints") {
    return [];
  }
  return [
    {
      controlIdSuffix: "endpoint1",
      pointRole: "endpoint1",
      sectionHeading: "Endpoint 1",
      sectionHeadingHasDivider: false,
      labelForAriaPrefix: "endpoint 1",
      pairAriaLabelPrefix: "coordinates for endpoint 1",
      xSliderAriaLabelPrefix: "endpoint 1 x coordinate",
      ySliderAriaLabelPrefix: "endpoint 1 y coordinate",
      xInputAriaLabelPrefix: "endpoint 1 x input",
      yInputAriaLabelPrefix: "endpoint 1 y input"
    },
    {
      controlIdSuffix: "endpoint2",
      pointRole: "endpoint2",
      sectionHeading: "Endpoint 2",
      sectionHeadingHasDivider: true,
      labelForAriaPrefix: "endpoint 2",
      pairAriaLabelPrefix: "coordinates for endpoint 2",
      xSliderAriaLabelPrefix: "endpoint 2 x coordinate",
      ySliderAriaLabelPrefix: "endpoint 2 y coordinate",
      xInputAriaLabelPrefix: "endpoint 2 x input",
      yInputAriaLabelPrefix: "endpoint 2 y input"
    }
  ];
}
function getLineSegmentCoordinatesByRole(lineSegment, pointRole) {
  if (pointRole === "endpoint1") {
    return lineSegment.endpoint1;
  }
  return lineSegment.endpoint2;
}
const LineSegmentControlsFamily = React__default.memo(function LineSegmentControlsFamily2({
  id,
  SVs,
  callAction,
  isGraphControlExpanded,
  toggleGraphControlExpanded
}) {
  const graphControlsMode = normalizeGraphControlsMode(SVs.addControls);
  if (graphControlsMode === "none") {
    return null;
  }
  const lineSegments = selectGraphControlsByType(
    SVs.graphicalDescendantsForControls,
    "lineSegment"
  );
  if (lineSegments.length === 0) {
    return null;
  }
  async function movePointLike({
    componentIdx,
    pointRole,
    x,
    y,
    transient,
    skippable
  }) {
    try {
      await callAction({
        action: {
          actionName: "moveLineSegmentSinglePoint",
          componentIdx
        },
        args: {
          x,
          y,
          pointRole,
          transient,
          skippable
        }
      });
    } catch (error) {
      console.error(
        `[graph-controls] moveLineSegmentSinglePoint failed for component ${componentIdx}`,
        error
      );
    }
  }
  const cards = lineSegments.map((lineSegment) => {
    const mode = normalizeLineSegmentControlsMode(
      lineSegment.addControls
    );
    const sections = getLineSegmentSections(mode);
    if (sections.length === 0) {
      return null;
    }
    const fallbackLabel = `Line segment ${lineSegment.lineSegmentNumber}`;
    const labelForAria = accessibleLabelText({
      label: lineSegment.label,
      labelHasLatex: lineSegment.labelHasLatex,
      fallback: fallbackLabel
    });
    const labelForDisplay = lineSegment.label.trim() ? renderLabelWithLatex({
      label: lineSegment.label,
      labelHasLatex: lineSegment.labelHasLatex
    }) : fallbackLabel;
    return /* @__PURE__ */ jsxRuntimeExports.jsx(
      ControlCard,
      {
        id: `${id}-lineSegment-${lineSegment.componentIdx}`,
        headingId: `${id}-lineSegment-${lineSegment.componentIdx}-heading`,
        contentId: `${id}-lineSegment-${lineSegment.componentIdx}-content`,
        heading: labelForDisplay,
        disclosureControlLabel: labelForAria,
        isExpanded: isGraphControlExpanded(
          lineSegment.componentIdx
        ),
        onToggleExpanded: () => toggleGraphControlExpanded(lineSegment.componentIdx),
        children: sections.map((section) => {
          const coordinates = getLineSegmentCoordinatesByRole(
            lineSegment,
            section.pointRole
          );
          return /* @__PURE__ */ jsxRuntimeExports.jsx(
            PointControlCoordinator,
            {
              id,
              controlId: `lineSegment-${lineSegment.componentIdx}-${section.controlIdSuffix}`,
              componentIdx: lineSegment.componentIdx,
              pointRole: section.pointRole,
              sectionHeading: section.sectionHeading,
              sectionHeadingHasDivider: section.sectionHeadingHasDivider,
              labelForAria: `${section.labelForAriaPrefix} for ${labelForAria}`,
              pairAriaLabel: `${section.pairAriaLabelPrefix} for ${labelForAria}`,
              xSliderAriaLabel: `${section.xSliderAriaLabelPrefix} for ${labelForAria}`,
              ySliderAriaLabel: `${section.ySliderAriaLabelPrefix} for ${labelForAria}`,
              xInputAriaLabel: `${section.xInputAriaLabelPrefix} for ${labelForAria}`,
              yInputAriaLabel: `${section.yInputAriaLabelPrefix} for ${labelForAria}`,
              graphControlsMode,
              pointControlsMode: "both",
              x: coordinates.x,
              y: coordinates.y,
              xMin: SVs.xMin,
              xMax: SVs.xMax,
              yMin: SVs.yMin,
              yMax: SVs.yMax,
              formatCoordinate: (value) => formatCoordinateForControls(
                value,
                lineSegment
              ),
              onMovePointLike: movePointLike
            },
            `${lineSegment.componentIdx}-${section.controlIdSuffix}`
          );
        })
      },
      lineSegment.componentIdx
    );
  }).filter((card) => Boolean(card));
  if (cards.length === 0) {
    return null;
  }
  return /* @__PURE__ */ jsxRuntimeExports.jsx(
    ControlsStack,
    {
      id: `${id}-lineSegments`,
      ariaLabel: "Line segment controls",
      children: cards
    }
  );
});
function getVectorSections(mode) {
  if (mode === "displacement") {
    return [
      {
        controlIdSuffix: "displacement",
        pointRole: "displacement",
        labelForAriaPrefix: "displacement",
        xAxisLabel: "Δx",
        yAxisLabel: "Δy",
        pairAriaLabelPrefix: "coordinates for displacement",
        xSliderAriaLabelPrefix: "displacement x",
        ySliderAriaLabelPrefix: "displacement y",
        xInputAriaLabelPrefix: "displacement x",
        yInputAriaLabelPrefix: "displacement y"
      }
    ];
  }
  if (mode === "headonly") {
    return [
      {
        controlIdSuffix: "head",
        pointRole: "head",
        labelForAriaPrefix: "head",
        xAxisLabel: "head x",
        yAxisLabel: "head y",
        pairAriaLabelPrefix: "coordinates for head",
        xSliderAriaLabelPrefix: "head x",
        ySliderAriaLabelPrefix: "head y",
        xInputAriaLabelPrefix: "head x",
        yInputAriaLabelPrefix: "head y"
      }
    ];
  }
  if (mode === "tailonly") {
    return [
      {
        controlIdSuffix: "tail",
        pointRole: "tail",
        labelForAriaPrefix: "tail",
        xAxisLabel: "tail x",
        yAxisLabel: "tail y",
        pairAriaLabelPrefix: "coordinates for tail",
        xSliderAriaLabelPrefix: "tail x",
        ySliderAriaLabelPrefix: "tail y",
        xInputAriaLabelPrefix: "tail x",
        yInputAriaLabelPrefix: "tail y"
      }
    ];
  }
  if (mode === "headandtail") {
    return [
      {
        controlIdSuffix: "head",
        pointRole: "head",
        labelForAriaPrefix: "head",
        xAxisLabel: "head x",
        yAxisLabel: "head y",
        pairAriaLabelPrefix: "coordinates for head",
        xSliderAriaLabelPrefix: "head x",
        ySliderAriaLabelPrefix: "head y",
        xInputAriaLabelPrefix: "head x",
        yInputAriaLabelPrefix: "head y",
        sectionHeading: "Head",
        sectionHeadingHasDivider: false
      },
      {
        controlIdSuffix: "tail",
        pointRole: "tail",
        labelForAriaPrefix: "tail",
        xAxisLabel: "tail x",
        yAxisLabel: "tail y",
        pairAriaLabelPrefix: "coordinates for tail",
        xSliderAriaLabelPrefix: "tail x",
        ySliderAriaLabelPrefix: "tail y",
        xInputAriaLabelPrefix: "tail x",
        yInputAriaLabelPrefix: "tail y",
        sectionHeading: "Tail",
        sectionHeadingHasDivider: true
      }
    ];
  }
  return [];
}
function getVectorCoordinatesByRole(vector, pointRole) {
  if (pointRole === "displacement") {
    return vector.displacement;
  }
  if (pointRole === "head") {
    return vector.head;
  }
  return vector.tail;
}
const VectorControlsFamily = React__default.memo(function VectorControlsFamily2({
  id,
  SVs,
  callAction,
  isGraphControlExpanded,
  toggleGraphControlExpanded
}) {
  const graphControlsMode = normalizeGraphControlsMode(SVs.addControls);
  if (graphControlsMode === "none") {
    return null;
  }
  const vectors = selectGraphControlsByType(
    SVs.graphicalDescendantsForControls,
    "vector"
  );
  if (vectors.length === 0) {
    return null;
  }
  async function movePointLike({
    componentIdx,
    pointRole,
    x,
    y,
    transient,
    skippable
  }) {
    try {
      await callAction({
        action: {
          actionName: "moveVectorSinglePoint",
          componentIdx
        },
        args: {
          x,
          y,
          pointRole,
          transient,
          skippable
        }
      });
    } catch (error) {
      console.error(
        `[graph-controls] moveVectorSinglePoint failed for component ${componentIdx}`,
        error
      );
    }
  }
  function formatValue(value, vector) {
    return formatCoordinateForControls(value, vector);
  }
  const cards = vectors.map((vector) => {
    const mode = normalizeVectorControlsMode(vector.addControls);
    if (mode === "none") {
      return null;
    }
    const fallbackLabel = `Vector ${vector.vectorNumber}`;
    const labelForAria = accessibleLabelText({
      label: vector.label,
      labelHasLatex: vector.labelHasLatex,
      fallback: fallbackLabel
    });
    const labelForDisplay = vector.label.trim() ? renderLabelWithLatex({
      label: vector.label,
      labelHasLatex: vector.labelHasLatex
    }) : fallbackLabel;
    const componentIdx = vector.componentIdx;
    const sections = getVectorSections(mode);
    if (sections.length === 0) {
      return null;
    }
    return /* @__PURE__ */ jsxRuntimeExports.jsx(
      ControlCard,
      {
        id: `${id}-vector-${componentIdx}`,
        headingId: `${id}-vector-${componentIdx}-heading`,
        contentId: `${id}-vector-${componentIdx}-content`,
        heading: labelForDisplay,
        disclosureControlLabel: labelForAria,
        isExpanded: isGraphControlExpanded(componentIdx),
        onToggleExpanded: () => toggleGraphControlExpanded(componentIdx),
        children: sections.map((section) => {
          const coordinates = getVectorCoordinatesByRole(
            vector,
            section.pointRole
          );
          return /* @__PURE__ */ jsxRuntimeExports.jsx(
            PointControlCoordinator,
            {
              id,
              controlId: `vector-${componentIdx}-${section.controlIdSuffix}`,
              componentIdx,
              pointRole: section.pointRole,
              sectionHeading: section.sectionHeading,
              sectionHeadingHasDivider: section.sectionHeadingHasDivider,
              labelForAria: `${section.labelForAriaPrefix} for ${labelForAria}`,
              xAxisLabel: section.xAxisLabel,
              yAxisLabel: section.yAxisLabel,
              pairAriaLabel: `${section.pairAriaLabelPrefix} for ${labelForAria}`,
              xSliderAriaLabel: `${section.xSliderAriaLabelPrefix} for ${labelForAria}`,
              ySliderAriaLabel: `${section.ySliderAriaLabelPrefix} for ${labelForAria}`,
              xInputAriaLabel: `${section.xInputAriaLabelPrefix} for ${labelForAria} input`,
              yInputAriaLabel: `${section.yInputAriaLabelPrefix} for ${labelForAria} input`,
              graphControlsMode,
              pointControlsMode: "both",
              x: coordinates.x,
              y: coordinates.y,
              xMin: SVs.xMin,
              xMax: SVs.xMax,
              yMin: SVs.yMin,
              yMax: SVs.yMax,
              formatCoordinate: (value) => formatValue(value, vector),
              onMovePointLike: movePointLike
            },
            `${componentIdx}-${section.controlIdSuffix}`
          );
        })
      },
      `${componentIdx}-${mode}`
    );
  }).filter((card) => Boolean(card));
  if (cards.length === 0) {
    return null;
  }
  return /* @__PURE__ */ jsxRuntimeExports.jsx(ControlsStack, { id: `${id}-vectors`, ariaLabel: "Vector controls", children: cards });
});
const CONTROLS_FAMILY_BY_TYPE = {
  point: PointControlsFamily,
  circle: CircleControlsFamily,
  polygon: PolygonControlsFamily,
  triangle: TriangleControlsFamily,
  regularPolygon: RegularPolygonControlsFamily,
  rectangle: RectangleControlsFamily,
  lineSegment: LineSegmentControlsFamily,
  vector: VectorControlsFamily
};
const GraphControlsRoot = React__default.memo(function GraphControlsRoot2(props) {
  const { id, SVs } = props;
  const orderedControls = React__default.useMemo(
    () => sortGraphControlsForDisplay(SVs.graphicalDescendantsForControls),
    [SVs.graphicalDescendantsForControls]
  );
  const defaultExpandedControlIds = React__default.useMemo(
    () => selectInitialExpandedGraphControlIds(orderedControls),
    [orderedControls]
  );
  const [expandedByControlId, setExpandedByControlId] = React__default.useState(() => {
    const initialMap = /* @__PURE__ */ new Map();
    for (const control of orderedControls) {
      initialMap.set(
        control.componentIdx,
        defaultExpandedControlIds.has(control.componentIdx)
      );
    }
    return initialMap;
  });
  React__default.useEffect(() => {
    setExpandedByControlId((previous) => {
      const next = /* @__PURE__ */ new Map();
      let changed = previous.size !== orderedControls.length;
      for (const control of orderedControls) {
        const componentIdx = control.componentIdx;
        const previousValue = previous.get(componentIdx);
        const nextValue = previousValue ?? defaultExpandedControlIds.has(componentIdx);
        next.set(componentIdx, nextValue);
        if (previousValue === void 0) {
          changed = true;
        }
      }
      if (!changed) {
        for (const [componentIdx, value] of previous) {
          if (next.get(componentIdx) !== value) {
            changed = true;
            break;
          }
        }
      }
      return changed ? next : previous;
    });
  }, [orderedControls, defaultExpandedControlIds]);
  const isGraphControlExpanded = React__default.useCallback(
    (componentIdx) => expandedByControlId.get(componentIdx) ?? defaultExpandedControlIds.has(componentIdx),
    [expandedByControlId, defaultExpandedControlIds]
  );
  const toggleGraphControlExpanded = React__default.useCallback(
    (componentIdx) => {
      setExpandedByControlId((previous) => {
        const next = new Map(previous);
        const currentValue = next.get(componentIdx) ?? defaultExpandedControlIds.has(componentIdx);
        next.set(componentIdx, !currentValue);
        return next;
      });
    },
    [defaultExpandedControlIds]
  );
  function renderControl(control) {
    const controlType = assertKnownGraphControlType(control.controlType);
    const FamilyComponent = CONTROLS_FAMILY_BY_TYPE[controlType];
    const instanceId = `${controlType}_${control.componentIdx}`;
    const controlFamilyProps = {
      ...props,
      id: `${id}_control_${instanceId}`,
      isGraphControlExpanded,
      toggleGraphControlExpanded,
      SVs: {
        ...SVs,
        graphicalDescendantsForControls: [control]
      }
    };
    return /* @__PURE__ */ jsxRuntimeExports.jsx(FamilyComponent, { ...controlFamilyProps }, instanceId);
  }
  return /* @__PURE__ */ jsxRuntimeExports.jsx("div", { id, children: orderedControls.map(renderControl) });
});
const BoardContext = createContext(null);
const MIN_GRAPH_WIDTH_FOR_SIDE_LAYOUT_PX = 280;
const SIDE_SLIDER_COLUMN_WIDTH_PX = 220;
const SIDE_LAYOUT_GAP_PX = 12;
const MIN_SIDE_LAYOUT_WIDTH_PX = MIN_GRAPH_WIDTH_FOR_SIDE_LAYOUT_PX + SIDE_SLIDER_COLUMN_WIDTH_PX + SIDE_LAYOUT_GAP_PX;
function normalizeControlsPosition(value) {
  if (value === "bottom" || value === "left" || value === "right" || value === "top") {
    return value;
  }
  return "left";
}
const graph = React__default.memo(function Graph(props) {
  let { id, SVs, children, ignoreUpdate, actions, callAction } = useDoenetRenderer(props);
  Graph.baseStateVariable = "boundingbox";
  const graphRenderer = SVs.effectiveRenderer ?? SVs.renderer;
  const isPrefigureRenderer = graphRenderer === "prefigure";
  const containerRef = useRef(null);
  const [availableWidth, setAvailableWidth] = useState(null);
  useRecordVisibilityChanges(
    containerRef,
    callAction,
    actions,
    SVs.haveGraphParent
  );
  useEffect(() => {
    if (SVs.haveGraphParent) {
      return;
    }
    return () => {
      callAction({
        action: actions.recordVisibilityChange,
        args: { isVisible: false }
      });
    };
  }, []);
  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    function updateContainerWidth() {
      setAvailableWidth(container.clientWidth);
    }
    updateContainerWidth();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateContainerWidth);
      return () => {
        window.removeEventListener("resize", updateContainerWidth);
      };
    }
    const observer = new ResizeObserver(() => {
      updateContainerWidth();
    });
    observer.observe(container);
    return () => {
      observer.disconnect();
    };
  }, []);
  const graphicalChildren = [...children];
  if (SVs.descriptionChildInd !== -1) {
    graphicalChildren.splice(SVs.descriptionChildInd, 1);
  }
  if (SVs.haveGraphParent) {
    return /* @__PURE__ */ jsxRuntimeExports.jsxs(jsxRuntimeExports.Fragment, { children: [
      /* @__PURE__ */ jsxRuntimeExports.jsx("span", { id }),
      graphicalChildren
    ] });
  }
  const descriptionChild = SVs.descriptionChildInd !== -1 && children[SVs.descriptionChildInd];
  const graphControlsMode = normalizeGraphControlsMode(SVs.addControls);
  const controlsEnabledAtGraphLevel = graphControlsMode !== "none";
  const hasGraphicalDescendantsForControls = Array.isArray(SVs.graphicalDescendantsForControls) && SVs.graphicalDescendantsForControls.length > 0;
  const shouldRenderControls = controlsEnabledAtGraphLevel && hasGraphicalDescendantsForControls;
  const requestedControlsPosition = shouldRenderControls ? normalizeControlsPosition(SVs.controlsPosition) : "left";
  const canUseSideLayout = availableWidth === null || availableWidth >= MIN_SIDE_LAYOUT_WIDTH_PX;
  const effectiveControlsPosition = requestedControlsPosition === "left" && !canUseSideLayout ? "top" : requestedControlsPosition === "right" && !canUseSideLayout ? "bottom" : requestedControlsPosition;
  const useSideLayout = shouldRenderControls && (effectiveControlsPosition === "left" || effectiveControlsPosition === "right");
  const layoutStyle = {
    display: "flex",
    flexDirection: useSideLayout ? "row" : "column",
    alignItems: useSideLayout ? "flex-start" : "stretch",
    gap: `${SIDE_LAYOUT_GAP_PX}px`,
    width: "100%",
    minWidth: 0
  };
  const graphSectionStyle = {
    order: effectiveControlsPosition === "top" || effectiveControlsPosition === "left" ? 2 : 1,
    flex: useSideLayout ? "1 1 auto" : void 0,
    width: useSideLayout ? void 0 : "100%",
    minWidth: useSideLayout ? `${MIN_GRAPH_WIDTH_FOR_SIDE_LAYOUT_PX}px` : 0
  };
  const controlsSectionStyle = {
    order: effectiveControlsPosition === "top" || effectiveControlsPosition === "left" ? 1 : 2,
    width: useSideLayout ? `${SIDE_SLIDER_COLUMN_WIDTH_PX}px` : "100%",
    maxWidth: useSideLayout ? `${SIDE_SLIDER_COLUMN_WIDTH_PX}px` : void 0,
    minWidth: 0
  };
  return /* @__PURE__ */ jsxRuntimeExports.jsx(
    GraphFrame,
    {
      id,
      SVs,
      isPrefigureRenderer,
      containerRef,
      descriptionChild,
      hasInteractiveControls: shouldRenderControls,
      suppressTopMargin: SVs.renderInlineForListItem,
      children: (surfaceStyle) => {
        const graphContent = isPrefigureRenderer ? /* @__PURE__ */ jsxRuntimeExports.jsx(Prefigure, { id, SVs, surfaceStyle }) : /* @__PURE__ */ jsxRuntimeExports.jsx(
          JSXGraphRenderer,
          {
            id,
            SVs,
            ignoreUpdate,
            actions,
            callAction,
            BoardContext,
            surfaceStyle,
            children: graphicalChildren
          }
        );
        if (!shouldRenderControls) {
          return graphContent;
        }
        return /* @__PURE__ */ jsxRuntimeExports.jsxs("div", { style: layoutStyle, children: [
          /* @__PURE__ */ jsxRuntimeExports.jsx("div", { style: graphSectionStyle, children: graphContent }),
          /* @__PURE__ */ jsxRuntimeExports.jsx("div", { style: controlsSectionStyle, children: /* @__PURE__ */ jsxRuntimeExports.jsx(
            GraphControlsRoot,
            {
              id: `${id}-controls`,
              SVs,
              callAction
            }
          ) })
        ] });
      }
    }
  );
});
let tempCounter = 0;
const BASE_LAYER_OFFSET = tempCounter++;
const IMAGE_LAYER_OFFSET = tempCounter++;
const LINE_LAYER_OFFSET = tempCounter++;
const VERTEX_LAYER_OFFSET = tempCounter++;
const CONTROL_POINT_LAYER_OFFSET = tempCounter++;
const POINT_LAYER_OFFSET = tempCounter++;
const TEXT_LAYER_OFFSET = tempCounter++;
export {
  BASE_LAYER_OFFSET,
  BoardContext,
  CONTROL_POINT_LAYER_OFFSET,
  IMAGE_LAYER_OFFSET,
  LINE_LAYER_OFFSET,
  POINT_LAYER_OFFSET,
  TEXT_LAYER_OFFSET,
  VERTEX_LAYER_OFFSET,
  graph as default
};
